import { useState, useEffect, useCallback } from 'react';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useAuth } from '../../context/AuthContext';
import { USER_ROLES } from '../../constants/userRoles';
import axiosInstance from '../../api/axiosInstance';
import swal from '../../utils/swal';
import {
  PERFIL_ROLE_LABELS,
  PERFIL_FIELDS_BY_ROLE,
  PERFIL_NOMBRE_FIELD,
} from './perfilConfig';
import Loader from '../../components/loading/Loader';
import './Perfil.css';

const EMPTY_FORM = {
  nombre: '',
  dni: '',
  obraSocial: '',
  plan: '',
  matricula: '',
  nombreUsuario: '',
  password: '',
};

function Perfil() {
  const { user, updateSession } = useAuth();
  const rol = user?.rol ?? USER_ROLES.PACIENTE;

  const [perfil, setPerfil] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const fields = PERFIL_FIELDS_BY_ROLE[rol] ?? PERFIL_FIELDS_BY_ROLE[USER_ROLES.PACIENTE];
  const roleLabel = PERFIL_ROLE_LABELS[rol] ?? rol;

  const mapPerfilToForm = useCallback((data) => ({
    nombre: data.nombre ?? '',
    dni: data.dni != null ? String(data.dni) : '',
    obraSocial: data.obraSocial ?? '',
    plan: data.plan ?? '',
    matricula: data.matricula ?? '',
    nombreUsuario: data.nombreUsuario ?? '',
    password: '',
  }), []);

  const cargarPerfil = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/perfil');
      setPerfil(data);
      setForm(mapPerfilToForm(data));
    } catch (err) {
      const mensaje = err.response?.data?.message ?? 'No se pudo cargar el perfil. Intentá de nuevo.';
      await swal.fire({ icon: 'error', title: 'Error', text: mensaje, confirmButtonColor: '#db1414' });
    } finally {
      setLoading(false);
    }
  }, [mapPerfilToForm]);

  useEffect(() => {
    cargarPerfil();
  }, [cargarPerfil]);

  function handleChange(key, value) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  function handleEditar() {
    setForm(mapPerfilToForm(perfil));
    setShowPassword(false);
    setEditing(true);
  }

  function handleCancelar() {
    setForm(mapPerfilToForm(perfil));
    setShowPassword(false);
    setEditing(false);
  }

  async function handleGuardar() {
    if (!form.nombre.trim()) {
      await swal.fire({ icon: 'warning', title: 'Campo requerido', text: 'El nombre completo es obligatorio.' });
      return;
    }

    const payload = {
      nombre: form.nombre.trim(),
      nombreUsuario: form.nombreUsuario.trim(),
    };

    if (rol === USER_ROLES.PACIENTE) {
      payload.dni = Number(form.dni);
      payload.obraSocial = form.obraSocial.trim();
      payload.plan = form.plan.trim();
    } else {
      payload.matricula = form.matricula.trim();
    }

    if (form.password.trim()) {
      payload.password = form.password;
    }

    setGuardando(true);
    try {
      const { data } = await axiosInstance.patch('/perfil', payload);
      setPerfil(data);
      setForm(mapPerfilToForm(data));
      setEditing(false);
      setShowPassword(false);
      updateSession({ nombreUsuario: data.nombreUsuario });
      await swal.fire({ icon: 'success', title: 'Perfil actualizado', confirmButtonColor: '#09a6a3' });
    } catch (err) {
      const mensaje = err.response?.data?.message ?? 'No se pudo guardar el perfil. Intentá de nuevo.';
      await swal.fire({ icon: 'error', title: 'Error', text: mensaje, confirmButtonColor: '#db1414' });
    } finally {
      setGuardando(false);
    }
  }

  function renderFieldValue(field) {
    if (field.key === 'password') {
      return '••••••••';
    }
    if (field.key === 'dni') {
      return perfil?.dni ?? '—';
    }
    return perfil?.[field.key] ?? '—';
  }

  if (loading) {
    return <Loader mensaje="Cargando perfil..." />;
  }

  if (!perfil) {
    return <p className="perfil__loading" role="alert">No se encontró el perfil.</p>;
  }

  const displayNombre = editing ? form.nombre : perfil.nombre;

  return (
    <div className="perfil">
      <div className="perfil__card">
        {!editing && (
          <button
            type="button"
            className="perfil__edit-btn"
            onClick={handleEditar}
            aria-label="Editar perfil"
          >
            <EditIcon />
          </button>
        )}

        <header className="perfil__header">
          <div className="perfil__avatar" aria-hidden="true">
            <AccountCircleIcon />
          </div>
          <h1 className="perfil__nombre">{displayNombre}</h1>
          <p className="perfil__rol">{roleLabel}</p>
        </header>

        <div className="perfil__body">
          {editing ? (
            <form
              className="perfil__form"
              onSubmit={e => { e.preventDefault(); handleGuardar(); }}
              noValidate
            >
              <div className="perfil__field">
                <label className="perfil__label" htmlFor="perfil-nombre">
                  {PERFIL_NOMBRE_FIELD.label}
                </label>
                <input
                  id="perfil-nombre"
                  className="perfil__input"
                  type="text"
                  value={form.nombre}
                  onChange={e => handleChange('nombre', e.target.value)}
                  required
                />
              </div>

              {fields.map(field => (
                <div key={field.key} className="perfil__field">
                  <label className="perfil__label" htmlFor={`perfil-${field.key}`}>
                    {field.label}
                  </label>
                  {field.type === 'password' ? (
                    <div className="perfil__input-wrapper">
                      <input
                        id={`perfil-${field.key}`}
                        className="perfil__input perfil__input--with-icon"
                        type={showPassword ? 'text' : 'password'}
                        value={form[field.key]}
                        onChange={e => handleChange(field.key, e.target.value)}
                        placeholder="Dejar vacío para no cambiar"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="perfil__pass-toggle"
                        onClick={() => setShowPassword(prev => !prev)}
                        aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </button>
                    </div>
                  ) : (
                    <input
                      id={`perfil-${field.key}`}
                      className="perfil__input"
                      type={field.type}
                      inputMode={field.inputMode}
                      value={form[field.key]}
                      onChange={e => handleChange(
                        field.key,
                        field.key === 'dni' ? e.target.value.replace(/\D/g, '') : e.target.value,
                      )}
                      required={field.key !== 'password'}
                      autoComplete={field.key === 'nombreUsuario' ? 'username' : undefined}
                    />
                  )}
                </div>
              ))}

              <div className="perfil__actions">
                <button
                  type="button"
                  className="perfil__btn perfil__btn--secondary"
                  onClick={handleCancelar}
                  disabled={guardando}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="perfil__btn perfil__btn--primary"
                  disabled={guardando}
                >
                  {guardando ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          ) : (
            <dl className="perfil__list">
              {fields.map(field => (
                <div key={field.key} className="perfil__list-item">
                  <dt className="perfil__list-label">{field.label}</dt>
                  <dd className="perfil__list-value">{renderFieldValue(field)}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </div>
    </div>
  );
}

export default Perfil;
